import type { TemplateMeta } from '@tsagko/label-contracts'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useAdapter, useAdapterMode } from '../adapters/AdapterProvider'
import { useConfirm } from '../ui/ConfirmDialog'
import { useToast } from '../ui/Toaster'
import { CardMenu } from './CardMenu'
import { deriveCopyId } from './copy-id'

export function TemplatesGallery() {
  const { t } = useTranslation()
  const adapter = useAdapter()
  const mode = useAdapterMode()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const toast = useToast()
  const [items, setItems] = useState<TemplateMeta[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!adapter) return
    let cancelled = false
    void (async () => {
      const res = await adapter.resources.listTemplates({ limit: 100 })
      if (cancelled) return
      if (res.status === 'ok') setItems(res.data.items)
      else setError(res.error.message)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [adapter])

  // Clear any pending debounce on unmount so setQuery can't fire afterwards.
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current) }, [])

  async function handleDuplicate(item: TemplateMeta) {
    if (!adapter) return
    const loadRes = await adapter.resources.loadTemplate(item.id)
    if (loadRes.status !== 'ok') {
      toast.error(t('toast.loadTemplateFailed', { message: loadRes.error.message }))
      return
    }
    const existingIds = new Set(items.map((i) => i.id))
    const newId = deriveCopyId(item.id, existingIds)
    const copy = {
      ...loadRes.data.resource,
      id: newId,
      name: `${item.name} (copy)`,
      updatedAt: undefined,
    }
    const saveRes = await adapter.resources.saveTemplate(copy)
    if (saveRes.status !== 'ok') {
      toast.error(t('toast.duplicateTemplateFailed', { message: saveRes.error.message }))
      return
    }
    navigate(`/templates/${encodeURIComponent(newId)}`)
  }

  async function handleDelete(item: TemplateMeta) {
    if (!adapter) return
    const confirmed = await confirm({
      title: t('confirm.deleteTemplate.title', { name: item.name }),
      body: t('confirm.deleteTemplate.body'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      danger: true,
    })
    if (!confirmed) return
    const res = await adapter.resources.deleteTemplate(item.id)
    if (res.status !== 'ok') {
      toast.error(t('toast.deleteTemplateFailed', { message: res.error.message }))
      return
    }
    setItems((prev) => prev.filter((i) => i.id !== item.id))
  }

  function handleSearchChange(value: string) {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setQuery(value)
    }, 150)
  }

  function clearSearch() {
    setSearchInput('')
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setQuery('')
  }

  const filteredItems = query.trim()
    ? items.filter((item) => {
        const q = query.toLowerCase()
        return (
          item.name.toLowerCase().includes(q) ||
          item.id.toLowerCase().includes(q)
        )
      })
    : items

  if (!adapter || loading) {
    return <div className="p-8 text-sm text-stone-500">{t('gallery.templates.loading')}</div>
  }

  return (
    <div className="h-full overflow-auto p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">{t('gallery.templates.title')}</h2>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            {t(
              mode === 'embedded' ? 'gallery.templates.subtitleEmbedded' : 'gallery.templates.subtitle',
              { count: items.length }
            )}
          </p>
        </div>
        <Link
          to="/templates/new"
          data-testid="gallery-new-button"
          className="rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
        >
          {t('gallery.templates.newTemplate')}
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded border border-rose-300 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300">
          {error}
        </div>
      )}

      {items.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <input
            data-testid="gallery-search"
            type="search"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('gallery.templates.search')}
            className="flex-1 rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-stone-500 dark:border-stone-700 dark:bg-stone-900"
          />
          <span data-testid="gallery-count" className="text-sm text-stone-500 dark:text-stone-400 whitespace-nowrap">
            {query.trim()
              ? t('gallery.common.countFiltered', {
                  shown: filteredItems.length,
                  total: items.length,
                })
              : t('gallery.templates.count', { count: items.length })}
          </span>
        </div>
      )}

      {items.length === 0 ? (
        <div
          data-testid="gallery-zero-state"
          className="rounded border border-dashed border-stone-300 p-12 text-center dark:border-stone-700"
        >
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {t('gallery.templates.emptyState')}
          </p>
          <Link
            to="/templates/new"
            data-testid="gallery-zero-state-new"
            className="mt-4 inline-block rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
          >
            {t('gallery.templates.newTemplate')}
          </Link>
        </div>
      ) : filteredItems.length === 0 ? (
        <div
          data-testid="gallery-no-matches"
          className="rounded border border-dashed border-stone-300 p-12 text-center dark:border-stone-700"
        >
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {t('gallery.common.noMatches', { query })}
          </p>
          <button
            data-testid="gallery-clear-search"
            type="button"
            onClick={clearSearch}
            className="mt-4 rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
          >
            {t('common.clear')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <Link
            to="/templates/new"
            className="flex aspect-[5/4] items-center justify-center rounded border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-stone-500 dark:border-stone-700 dark:text-stone-400"
          >
            {t('gallery.templates.startFromBlank')}
          </Link>
          {filteredItems.map(item => (
            <Link
              key={item.id}
              to={`/templates/${encodeURIComponent(item.id)}`}
              data-testid="gallery-item"
              data-name={item.name}
              className="relative flex aspect-[5/4] flex-col justify-between rounded border border-stone-200 bg-white p-4 text-left shadow-sm transition hover:border-stone-400 dark:border-stone-800 dark:bg-stone-900"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold">{item.name}</h3>
                  <p className="mt-1 text-[11px] text-stone-500 dark:text-stone-400">
                    {item.widthMm} × {item.heightMm} mm
                  </p>
                  {item.description && (
                    <p className="mt-2 text-xs text-stone-600 dark:text-stone-400 line-clamp-2">
                      {item.description}
                    </p>
                  )}
                </div>
                <CardMenu
                  items={[
                    {
                      id: 'duplicate',
                      label: t('common.duplicate'),
                      onSelect: () => void handleDuplicate(item),
                    },
                    {
                      id: 'delete',
                      label: t('common.delete'),
                      onSelect: () => void handleDelete(item),
                      danger: true,
                    },
                  ]}
                />
              </div>
              <p className="text-[10px] text-stone-400">
                {item.updatedAt?.slice(0, 10) ?? '—'}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
